import type { CoilPropTemplate } from '../../../../shared/scene/coilPropTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawMasonryBlocks } from './propDrawingPrimitives';

/** Stepped altar with a stone table on two pierced supports. The little spiral
 * is an inset engraving on its top; there is no luminous disc or broad halo. */
function altar(p: TownPropPalette): string {
  return `<g data-part="eldritch-altar">
    <path d="M8 55L19 48H141L152 55V64H8Z" fill="${hex(p.shade)}" />
    <path d="M8 55H152L141 48H19Z" fill="${hex(p.stone)}" />
    <path d="M8 59H152V64H8Z" fill="${hex(p.primary)}" />
    <path d="M22 44L31 39H129L138 44V52H22Z" fill="${hex(p.primary)}" />
    <path d="M22 44H138L129 39H31Z" fill="${hex(p.roofEdge)}" />
    <path d="M24 47H136" stroke="${hex(p.shade)}" stroke-width="2" />
    ${drawMasonryBlocks({ x: 38, y: 23, width: 23, height: 20 }, p, { courses: 2, columns: 1 })}
    ${drawMasonryBlocks({ x: 99, y: 23, width: 23, height: 20 }, p, { courses: 2, columns: 1 })}
    <path d="M27 17H139V25H27Z" fill="${hex(p.shade)}" />
    <path d="M27 17L39 8H127L139 17Z" fill="${hex(p.stone)}" />
    <path d="M31 18H135V23H31Z" fill="${hex(p.primary)}" />
    <path d="M42 11H121 M34 16H130" stroke="${hex(p.roofEdge)}" stroke-width="1.1" />
    <path data-part="altar-incision" d="M69 16L65 13L73 10H88L97 13L90 16H77L73 13H86"
      fill="none" stroke="${hex(p.glass)}" stroke-width="1.2" />
    <path d="M48 20H54 M77 20H86 M111 20H117" stroke="${hex(p.glassCore)}" stroke-width="1" opacity=".7" />
    <path d="M31 50L38 47L44 50 M118 50L125 47L132 50" fill="none" stroke="${hex(p.shade)}" stroke-width="1.4" />
    <path d="M19 60H48 M110 60H141" stroke="${hex(p.roofEdge)}" stroke-width="1" opacity=".7" />
    <path d="M134 55L130 59L135 64H142L137 59L143 55Z" fill="${hex(p.shade)}" />
  </g>`;
}

/** Narrow fluted shaft with staggered broken seams. Separate facets stay
 * readable at low contrast; green occupies only short recessed incisions. */
function column(p: TownPropPalette): string {
  return `<g data-part="cracked-colonnade">
    <path d="M5 168L12 161H44L51 168V176H5Z" fill="${hex(p.primary)}" />
    <path d="M5 168H51L44 161H12Z" fill="${hex(p.stone)}" />
    <path d="M7 172H49" stroke="${hex(p.roofEdge)}" stroke-width="1.4" />
    <path d="M11 153H45V165H11Z" fill="${hex(p.shade)}" />
    <path d="M13 152H43V158H13Z" fill="${hex(p.stone)}" />
    <path d="M15 29H41V61L38 66L41 70V106L37 112L40 116V152H15V117L18 113L15 109V72L17 68L15 64Z"
      fill="${hex(p.primary)}" />
    <path d="M33 29H41V61L38 66L41 70V106L37 112L40 116V152H33Z" fill="${hex(p.shade)}" />
    <path d="M18 32V61 M18 76V106 M19 122V148 M25 32V62 M25 76V108 M25 120V148
      M32 32V59 M32 77V103 M32 121V148" fill="none" stroke="${hex(p.stone)}" stroke-width="2" />
    <path d="M20 33V60 M27 77V105 M21 122V147" stroke="${hex(p.roofEdge)}" stroke-width=".8" opacity=".55" />
    <path d="M15 64L25 67L31 63L39 68 M16 111L24 115L30 109L39 114"
      fill="none" stroke="${hex(p.iron)}" stroke-width="3" />
    <path data-part="column-incisions" d="M26 65L30 63L34 65 M25 114L29 110L31 114L29 122 M20 45L22 49L20 53"
      fill="none" stroke="${hex(p.glass)}" stroke-width="1.2" opacity=".8" />
    <path d="M12 22H44L40 32H16Z" fill="${hex(p.stone)}" />
    <path d="M8 12H48V22H8Z" fill="${hex(p.primary)}" />
    <path d="M6 6L17 3H26L29 7L34 4H47L50 6V12H6Z" fill="${hex(p.stone)}" />
    <path d="M9 14H45 M16 25H40" stroke="${hex(p.roofEdge)}" stroke-width="1.3" />
    <path d="M30 8L27 13L31 17L28 22" fill="none" stroke="${hex(p.shade)}" stroke-width="2.5" />
  </g>`;
}

/** Carved bowl with three separated static wisps. Open air remains between
 * the wisps and beside the pedestal; no filter, animation or glow rectangle. */
function brazier(p: TownPropPalette): string {
  return `<g data-part="void-brazier">
    <g data-part="brazier-wisps">
      <path d="M29 49Q19 37 28 26Q35 18 30 4Q46 17 37 31Q30 39 36 48Z" fill="${hex(p.glass)}" opacity=".65" />
      <path d="M31 47Q26 38 33 28Q39 19 35 13Q42 21 35 32Q30 40 34 47Z" fill="${hex(p.glassCore)}" opacity=".7" />
      <path d="M18 48Q10 42 15 32Q18 26 16 21Q25 29 20 37Q18 42 23 48Z" fill="${hex(p.glass)}" opacity=".48" />
      <path d="M43 48Q48 40 44 32Q43 27 48 21Q47 28 52 33Q56 42 48 49Z" fill="${hex(p.glass)}" opacity=".55" />
    </g>
    <path d="M10 97L18 91H46L54 97V104H10Z" fill="${hex(p.primary)}" />
    <path d="M10 97H54L46 91H18Z" fill="${hex(p.stone)}" />
    <path d="M23 62H41L38 73L41 92H23L26 74Z" fill="${hex(p.primary)}" />
    <path d="M33 63H41L38 73L41 92H34L30 75Z" fill="${hex(p.shade)}" />
    <path d="M27 67L29 76L27 88" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.5" />
    <path d="M22 89H42V95H22Z" fill="${hex(p.stone)}" />
    <path d="M8 46L14 42H50L56 46L48 60L40 66H24L16 60Z" fill="${hex(p.primary)}" />
    <path d="M8 46L14 42H50L56 46L48 51H16Z" fill="${hex(p.roofEdge)}" />
    <path d="M15 46L20 44H46L50 46L45 48H21Z" fill="${hex(p.iron)}" />
    <path d="M31 52H51L46 59L39 64H31Z" fill="${hex(p.shade)}" />
    <path d="M15 51L21 60 M23 53L26 62 M41 53L38 62" stroke="${hex(p.stone)}" stroke-width="2" />
    <path d="M29 55L32 58L35 55 M32 58V62" fill="none" stroke="${hex(p.glass)}" stroke-width="1.2" />
    <path d="M15 100H25 M39 100H49" stroke="${hex(p.roofEdge)}" stroke-width="1.2" />
  </g>`;
}

/** Low toppled paving with an uneven fractured edge, inset angular script and
 * a small separate chip. The top face is solid stone, never a light panel. */
function slab(p: TownPropPalette): string {
  return `<g data-part="runic-slab">
    <path d="M4 24L15 10L38 4L79 7L95 18L91 32H7Z" fill="${hex(p.shade)}" />
    <path d="M4 24L15 10L38 4L79 7L95 18L85 25L64 23L55 27L29 24L17 28Z" fill="${hex(p.stone)}" />
    <path d="M17 13L39 7L77 10L87 18L81 22L62 20L53 24L29 21L18 24L10 22Z" fill="${hex(p.primary)}" />
    <path d="M8 26L18 30L29 26L54 29L65 26L86 28L91 24L90 32H7Z" fill="${hex(p.roof)}" />
    <path d="M16 13L38 7L76 10 M13 22L20 24" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.1" />
    <path data-part="slab-inscription" d="M29 14L34 10L39 14L34 18Z M46 11L43 18L49 20L52 13
      M59 13L65 13L62 17L68 20 M74 14L78 18L73 21"
      fill="none" stroke="${hex(p.glass)}" stroke-width="1.3" opacity=".85" />
    <path d="M56 6L53 12L57 16L52 21L55 26 M81 9L77 13L82 17" fill="none" stroke="${hex(p.shade)}" stroke-width="1.8" />
    <path d="M96 28L99 24L103 28V32H96Z" fill="${hex(p.stone)}" />
    <path d="M97 29H103V32H97Z" fill="${hex(p.shade)}" />
  </g>`;
}

export function buildCoilPropBody(template: CoilPropTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'eldritch_altar': return altar(palette);
  case 'cracked_colonnade': return column(palette);
  case 'void_brazier': return brazier(palette);
  case 'runic_slab': return slab(palette);
  }
}
